import { domainError } from '../errors.js';
import { approxEqual, approxAtMost } from '../numeric.js';
import { err, ok } from '../result.js';
const keyOf = (incorporation) => incorporation.item === 'water' ? 'water' : `${incorporation.item}:${incorporation.id}`;
/** Final-mix items and their masses, keyed like incorporations. */
const finalMixItems = (formula) => new Map([
    ...Object.entries(formula.finalMix.flourByFlour).map(([id, g]) => [`flour:${id}`, g]),
    ...Object.entries(formula.finalMix.grams).map(([id, g]) => [`ingredient:${id}`, g]),
    ...Object.entries(formula.finalMix.componentMasses).map(([id, g]) => [`component:${id}`, g]),
    ['water', formula.finalMix.water],
]);
/**
 * Grams added at each step. Returns an empty plan when the process declares no incorporation;
 * otherwise every final-mix item with a positive mass must be incorporated exactly once.
 */
export const planIncorporations = (process, formula) => {
    const declared = process.steps.some((step) => (step.incorporations ?? []).length > 0);
    if (!declared)
        return ok({});
    const items = finalMixItems(formula);
    const shares = new Map();
    const plan = new Map();
    for (const [index, step] of process.steps.entries()) {
        for (const [i, incorporation] of (step.incorporations ?? []).entries()) {
            const key = keyOf(incorporation);
            const mass = items.get(key);
            if (mass === undefined) {
                const id = incorporation.item === 'water' ? 'water' : incorporation.id;
                return err(domainError('UnknownReference', `${key} is not a final-mix item`, {
                    path: `steps[${index}].incorporations[${i}]`,
                    id,
                }));
            }
            shares.set(key, (shares.get(key) ?? 0) + incorporation.sharePct);
            const amounts = plan.get(step.id) ?? [];
            plan.set(step.id, amounts);
            amounts.push({
                item: incorporation.item,
                ...(incorporation.item === 'water' ? {} : { id: incorporation.id }),
                grams: (mass * incorporation.sharePct) / 100,
            });
        }
    }
    for (const [key, mass] of items) {
        const share = shares.get(key) ?? 0;
        const complete = mass <= 0 ? approxAtMost(share, 100) : approxEqual(share, 100);
        if (!complete) {
            return err(domainError('IncompleteIncorporation', `${key} is incorporated at ${share} % instead of 100 %`, {
                item: key,
                sharePct: share,
            }));
        }
    }
    return ok(Object.fromEntries(plan));
};
//# sourceMappingURL=incorporations.js.map